// Shared vocabulary for the VEXOS Executive dashboard: the certainty tiers the
// backend speaks (routers/vexos_executive.py) and the formatting every panel
// uses. Tier colors are a sequential estate-green ramp — the tiers are an
// ORDERED certainty scale of one measure, so light→dark encodes most→least
// certain rather than four unrelated hues. Adjacent steps were validated at
// ΔE ≥ 18.9 (normal + CVD) on both surfaces; the low-contrast light steps get
// their relief from the legend, tooltips and the table view.

export const TIER_ORDER = ["active", "ready", "signed", "forecasted"];

export const TIER_META = {
  active: {
    label: "Billing",
    long: "Active — billing now",
    light: "#14231D",
    dark: "#E4EAE6",
  },
  ready: {
    label: "Ready to bill",
    long: "Delivered, billing not started",
    light: "#3D6355",
    dark: "#A9BCB2",
  },
  signed: {
    label: "Signed",
    long: "Signed, platform in delivery",
    light: "#7E9C8E",
    dark: "#6E8A7D",
  },
  forecasted: {
    label: "Forecasted",
    long: "On the Horizon — not signed",
    light: "#CBD5CE",
    dark: "#3D6355",
  },
  paused: { label: "Paused", long: "Billing suspended" },
  completed: { label: "Completed", long: "Every month settled" },
  cancelled: { label: "Cancelled", long: "Terminated early" },
};

export function tierColor(tier, theme) {
  const meta = TIER_META[tier];
  if (!meta) return theme === "dark" ? "#8A8783" : "#9E9B96";
  return theme === "dark" ? meta.dark : meta.light;
}

export function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

export function formatCompact(value) {
  const v = value || 0;
  if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `$${Math.round(v / 1000)}K`;
  return formatCurrency(v);
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

// "2026-09" → "Sep '26" / "September 2026". Month keys never touch Date() —
// new Date("2026-09") parses as UTC midnight and shifts a month backward in
// western timezones.
export function monthShort(key) {
  if (!key) return "—";
  return `${MONTHS_SHORT[parseInt(key.slice(5, 7), 10) - 1]} '${key.slice(2, 4)}`;
}

export function monthLong(key) {
  if (!key) return "—";
  return `${MONTHS_LONG[parseInt(key.slice(5, 7), 10) - 1]} ${key.slice(0, 4)}`;
}

export function formatDate(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  return `${MONTHS_SHORT[parseInt(s.slice(5, 7), 10) - 1]} ${parseInt(s.slice(8, 10), 10)}, ${s.slice(0, 4)}`;
}
